import React, { useState, useEffect, useRef, useCallback } from "react";
import { getSupabase } from "../../utils/supabaseClient";
import {
  listMediaFolder,
  createMediaFolder,
  uploadMediaFile,
  removeMediaFiles,
  moveMediaFile,
} from "../../api/storageUploadsAPI";
import {
  getCardBackground,
  getPageBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getLightBorder,
  getInputStyles,
} from "../../utils/themeUtils";
import { toast } from "react-toastify";
import { captureError } from "../../utils/sentry";
import ShareDialog from "./ShareDialog";
import Tooltip from "./Tooltip";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { Button } from "../ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MediaUploadsPanelProps {
  folders: string[];
  selectedFolder: string;
  setSelectedFolder: (f: string) => void;
  onSelect: (imageUrl: string) => void;
  selectedImage?: string;
  parentName?: string;
  isDarkMode: boolean;
  getFolderDisplayName: (folder: string) => string;
  userDetails: Record<string, unknown>;
}

interface StorageFile {
  id: string | null;
  name: string;
  created_at?: string;
  updated_at?: string;
  metadata?: { size?: number; mimetype?: string; [key: string]: unknown };
}

type SortColumn = "name" | "created_at" | "size";
type SortOrder = "asc" | "desc";
type ViewMode = "grid" | "list";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatBytes = (bytes: number | undefined): string => {
  if (bytes == null || bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const isFolder = (f: StorageFile): boolean => f.id === null;

const isImageMime = (mime?: string): boolean =>
  !!mime && mime.startsWith("image/");

// ---------------------------------------------------------------------------
// Small sub-components
// ---------------------------------------------------------------------------

const FolderIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const FileIcon: React.FC<{ className?: string }> = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const Spinner: React.FC = () => (
  <svg className="animate-spin h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z" />
  </svg>
);

// ---------------------------------------------------------------------------
// Rename Modal
// ---------------------------------------------------------------------------

const RenameModal: React.FC<{
  isDarkMode: boolean;
  currentName: string;
  onConfirm: (newName: string) => void;
  onCancel: () => void;
}> = ({ isDarkMode, currentName, onConfirm, onCancel }) => {
  const [value, setValue] = useState(currentName);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40">
      <div className={`${getCardBackground(isDarkMode)} rounded-lg shadow-xl p-6 w-96 max-w-[90vw]`}>
        <h3 className={`text-lg font-semibold ${getPrimaryText(isDarkMode)} mb-4`}>Rename</h3>
        <input aria-label="New name"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && value.trim()) onConfirm(value.trim()); }}
          className={`w-full px-3 py-2 rounded-md text-sm ${getInputStyles(isDarkMode)} focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel}
            className={`px-3 py-1.5 text-sm rounded-md ${isDarkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-100"}`}>
            Cancel
          </button>
          <button onClick={() => value.trim() && onConfirm(value.trim())}
            disabled={!value.trim() || value.trim() === currentName}
            className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            Rename
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Move Modal
// ---------------------------------------------------------------------------

const MoveModal: React.FC<{
  isDarkMode: boolean;
  folders: string[];
  currentFolder: string;
  getFolderDisplayName: (f: string) => string;
  onConfirm: (targetFolder: string) => void;
  onCancel: () => void;
}> = ({ isDarkMode, folders, currentFolder, getFolderDisplayName, onConfirm, onCancel }) => {
  const [target, setTarget] = useState("");

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40">
      <div className={`${getCardBackground(isDarkMode)} rounded-lg shadow-xl p-6 w-96 max-w-[90vw]`}>
        <h3 className={`text-lg font-semibold ${getPrimaryText(isDarkMode)} mb-4`}>Move to folder</h3>
        <div className="space-y-1 max-h-60 overflow-auto">
          {folders.filter((f) => f !== currentFolder).map((folder) => (
            <button key={folder}
              onClick={() => setTarget(folder)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                target === folder
                  ? "bg-blue-100 text-blue-700 font-medium"
                  : isDarkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-700 hover:bg-gray-100"
              }`}>
              <FolderIcon className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{getFolderDisplayName(folder)}</span>
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onCancel}
            className={`px-3 py-1.5 text-sm rounded-md ${isDarkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-100"}`}>
            Cancel
          </button>
          <button onClick={() => target && onConfirm(target)}
            disabled={!target}
            className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            Move
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const MediaUploadsPanel: React.FC<MediaUploadsPanelProps> = ({
  folders,
  selectedFolder,
  setSelectedFolder,
  onSelect,
  selectedImage,
  parentName,
  isDarkMode,
  getFolderDisplayName,
  userDetails,
}) => {
  const { confirm, confirmModal } = useConfirmModal();

  // -- State ----------------------------------------------------------------
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [subPath, setSubPath] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Inline new-folder input
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  // Modals
  const [renameTarget, setRenameTarget] = useState<StorageFile | null>(null);
  const [shareTarget, setShareTarget] = useState<{ mediaId?: string; folderPath?: string; fileName?: string } | null>(null);
  const [moveTarget, setMoveTarget] = useState<StorageFile | null>(null);

  // Upload
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // -- Derived values -------------------------------------------------------
  const currentPath = subPath.length > 0
    ? `${selectedFolder}/${subPath.join("/")}`
    : selectedFolder;

  // A public URL is built locally (the bucket is public); every list, upload,
  // move and delete goes through the backend (PM 65d0edd2).
  const getPublicUrl = useCallback((filePath: string): string => {
    return getSupabase().storage.from("media").getPublicUrl(filePath).data.publicUrl;
  }, []);

  // -- Load files -----------------------------------------------------------
  const loadFiles = useCallback(async () => {
    if (!selectedFolder) return;
    setLoading(true);
    try {
      const supabaseSortCol = sortColumn === "size" ? "name" : sortColumn;
      const data = await listMediaFolder(currentPath, {
        sortBy: supabaseSortCol,
        order: sortOrder,
      });
      const filtered = (data || []).filter((f: StorageFile) => f.name !== ".emptyFolderPlaceholder");
      setFiles(filtered);
    } catch (err) {
      captureError(err, { component: "MediaUploadsPanel", operation: "loadFiles" });
      toast.error("Failed to load files.");
    } finally {
      setLoading(false);
    }
  }, [selectedFolder, currentPath, sortColumn, sortOrder]);

  useEffect(() => {
    setSubPath([]);
    setSelectedIds(new Set());
    setSearchQuery("");
  }, [selectedFolder]);

  useEffect(() => {
    loadFiles();
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, sortColumn, sortOrder]);

  // -- Filtered & sorted list -----------------------------------------------
  const visibleFiles = React.useMemo(() => {
    let list = [...files];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((f) => f.name.toLowerCase().includes(q));
    }
    // Client-side sort by size (Supabase doesn't support size sort directly)
    if (sortColumn === "size") {
      list.sort((a, b) => {
        const sA = a.metadata?.size ?? 0;
        const sB = b.metadata?.size ?? 0;
        return sortOrder === "asc" ? sA - sB : sB - sA;
      });
    }
    // Folders first
    list.sort((a, b) => {
      if (isFolder(a) && !isFolder(b)) return -1;
      if (!isFolder(a) && isFolder(b)) return 1;
      return 0;
    });
    return list;
  }, [files, searchQuery, sortColumn, sortOrder]);

  // -- Bulk selection helpers -----------------------------------------------
  const toggleSelect = (name: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === visibleFiles.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visibleFiles.map((f) => f.name)));
    }
  };

  // -- Navigate into subfolder ----------------------------------------------
  const navigateInto = (folderName: string) => {
    setSubPath((prev) => [...prev, folderName]);
    setSearchQuery("");
  };

  const navigateToBreadcrumb = (index: number) => {
    // index -1 = root (selectedFolder), 0 = first sub, etc.
    if (index < 0) {
      setSubPath([]);
    } else {
      setSubPath((prev) => prev.slice(0, index + 1));
    }
  };

  // -- Create folder --------------------------------------------------------
  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      await createMediaFolder(`${currentPath}/${name}`);
      toast.success(`Folder "${name}" created.`);
      setCreatingFolder(false);
      setNewFolderName("");
      loadFiles();
    } catch (err) {
      captureError(err, { component: "MediaUploadsPanel", operation: "createFolder" });
      toast.error("Failed to create folder.");
    }
  };

  // -- Upload files ---------------------------------------------------------
  const uploadFiles = async (fileList: FileList | File[]) => {
    const arr = Array.from(fileList);
    if (arr.length === 0) return;
    setIsUploading(true);
    let successCount = 0;
    try {
      for (const file of arr) {
        try {
          await uploadMediaFile(currentPath, file);
          successCount++;
        } catch (error) {
          captureError(error, { component: "MediaUploadsPanel", operation: "upload" });
          toast.error(`Failed to upload ${file.name}.`);
        }
      }
      if (successCount > 0) {
        toast.success(`${successCount} file${successCount > 1 ? "s" : ""} uploaded.`);
        loadFiles();
      }
    } catch (err) {
      captureError(err, { component: "MediaUploadsPanel", operation: "upload" });
      toast.error("Upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  // -- Delete ---------------------------------------------------------------
  const handleDeleteSingle = async (file: StorageFile) => {
    if (!(await confirm({
      title: "Delete file?",
      message: `Delete "${file.name}"?`,
      confirmText: "Delete",
    }))) return;
    try {
      const fullPath = `${currentPath}/${file.name}`;
      await removeMediaFiles([fullPath]);
      toast.success(`"${file.name}" deleted.`);
      loadFiles();
    } catch (err) {
      captureError(err, { component: "MediaUploadsPanel", operation: "delete" });
      toast.error("Delete failed.");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!(await confirm({
      title: "Delete files?",
      message: `Delete ${selectedIds.size} item(s)?`,
      confirmText: "Delete",
    }))) return;
    try {
      const paths = Array.from(selectedIds).map((name) => `${currentPath}/${name}`);
      await removeMediaFiles(paths);
      toast.success(`${selectedIds.size} item(s) deleted.`);
      setSelectedIds(new Set());
      loadFiles();
    } catch (err) {
      captureError(err, { component: "MediaUploadsPanel", operation: "bulkDelete" });
      toast.error("Bulk delete failed.");
    }
  };

  // -- Rename ---------------------------------------------------------------
  const handleRename = async (newName: string) => {
    if (!renameTarget) return;
    try {
      const oldPath = `${currentPath}/${renameTarget.name}`;
      const newPath = `${currentPath}/${newName}`;
      await moveMediaFile(oldPath, newPath);
      toast.success(`Renamed to "${newName}".`);
      setRenameTarget(null);
      loadFiles();
    } catch (err) {
      captureError(err, { component: "MediaUploadsPanel", operation: "rename" });
      toast.error("Rename failed.");
    }
  };

  // -- Move -----------------------------------------------------------------
  const handleMove = async (targetFolder: string) => {
    if (!moveTarget) return;
    try {
      const oldPath = `${currentPath}/${moveTarget.name}`;
      const newPath = `${targetFolder}/${moveTarget.name}`;
      await moveMediaFile(oldPath, newPath);
      toast.success(`Moved "${moveTarget.name}" to ${getFolderDisplayName(targetFolder)}.`);
      setMoveTarget(null);
      loadFiles();
    } catch (err) {
      captureError(err, { component: "MediaUploadsPanel", operation: "move" });
      toast.error("Move failed.");
    }
  };

  const handleBulkMove = async (targetFolder: string) => {
    try {
      let successCount = 0;
      for (const name of Array.from(selectedIds)) {
        const oldPath = `${currentPath}/${name}`;
        const newPath = `${targetFolder}/${name}`;
        try {
          await moveMediaFile(oldPath, newPath);
          successCount++;
        } catch (error) {
          captureError(error, { component: "MediaUploadsPanel", operation: "bulkMove" });
        }
      }
      if (successCount > 0) {
        toast.success(`${successCount} item(s) moved.`);
      }
      setSelectedIds(new Set());
      setMoveTarget(null);
      loadFiles();
    } catch (err) {
      captureError(err, { component: "MediaUploadsPanel", operation: "bulkMove" });
      toast.error("Move failed.");
    }
  };

  // Bulk-move uses a separate flag since MoveModal expects a single target
  const [bulkMoving, setBulkMoving] = useState(false);

  // -- Drag & drop handlers -------------------------------------------------
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  // -- Render helpers -------------------------------------------------------
  const renderBreadcrumb = () => {
    const rootLabel = getFolderDisplayName(selectedFolder);
    return (
      <nav className="flex items-center gap-1 text-sm flex-shrink-0">
        <button onClick={() => navigateToBreadcrumb(-1)}
          className={`hover:underline font-medium ${subPath.length === 0 ? getPrimaryText(isDarkMode) : "text-blue-500"}`}>
          {rootLabel}
        </button>
        {subPath.map((seg, i) => (
          <React.Fragment key={i}>
            <span className={getMutedText(isDarkMode)}>/</span>
            <button onClick={() => navigateToBreadcrumb(i)}
              className={`hover:underline ${i === subPath.length - 1 ? getPrimaryText(isDarkMode) + " font-medium" : "text-blue-500"}`}>
              {seg}
            </button>
          </React.Fragment>
        ))}
      </nav>
    );
  };

  const renderFileCard = (file: StorageFile) => {
    const folder = isFolder(file);
    const fullPath = `${currentPath}/${file.name}`;
    const imageUrl = folder ? "" : getPublicUrl(fullPath);
    const isSelected = selectedIds.has(file.name);
    const isImage = !folder && isImageMime(file.metadata?.mimetype as string | undefined);

    return (
      <div key={file.name} className="relative group">
        {/* Checkbox */}
        <div className="absolute top-2 left-2 z-10">
          <input aria-label={`Select ${file.name}`} type="checkbox" checked={isSelected}
            onChange={() => toggleSelect(file.name)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />
        </div>

        <div
          onClick={() => {
            if (folder) { navigateInto(file.name); return; }
            onSelect(imageUrl);
          }}
          className={`cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:shadow-md ${
            !folder && selectedImage === imageUrl
              ? "border-blue-500 ring-2 ring-blue-200"
              : isSelected
              ? "border-blue-400"
              : isDarkMode
              ? "border-gray-600 hover:border-gray-500"
              : "border-gray-200 hover:border-gray-300"
          }`}
        >
          <div className={`aspect-square ${isDarkMode ? "bg-gray-700" : "bg-gray-100"} flex items-center justify-center relative`}>
            {folder ? (
              <FolderIcon className={`w-12 h-12 ${isDarkMode ? "text-yellow-400" : "text-yellow-500"}`} />
            ) : isImage ? (
              <>
                <img src={imageUrl} alt={file.name} className="w-full h-full object-cover"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                <FileIcon className={`w-8 h-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"} absolute`} />
              </>
            ) : (
              <FileIcon className={`w-10 h-10 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
            )}
          </div>
          <div className={`p-2 ${getCardBackground(isDarkMode)}`}>
            <p className={`text-xs ${getSecondaryText(isDarkMode)} truncate font-medium`}>{file.name}</p>
            {!folder && file.metadata?.size != null && (
              <p className={`text-xs ${getMutedText(isDarkMode)}`}>{formatBytes(file.metadata.size as number)}</p>
            )}
          </div>
          {!folder && selectedImage === imageUrl && (
            <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
              <CheckIcon />
            </div>
          )}
        </div>

        {/* Hover actions */}
        {!folder && (
          <div className="absolute top-2 right-2 hidden group-hover:flex items-center gap-1">
            <Tooltip content="Rename"><button aria-label="Rename" onClick={(e) => { e.stopPropagation(); setRenameTarget(file); }}

              className={`p-1 rounded ${isDarkMode ? "bg-gray-800 text-gray-300 hover:bg-gray-700" : "bg-white text-gray-600 hover:bg-gray-100"} shadow-sm`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button></Tooltip>
            <Tooltip content="Move"><button aria-label="Move" onClick={(e) => { e.stopPropagation(); setMoveTarget(file); }}

              className={`p-1 rounded ${isDarkMode ? "bg-gray-800 text-gray-300 hover:bg-gray-700" : "bg-white text-gray-600 hover:bg-gray-100"} shadow-sm`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
            </button></Tooltip>
            <Tooltip content="Share"><button aria-label="Share" onClick={(e) => { e.stopPropagation(); setShareTarget({ fileName: file.name }); }}

              className={`p-1 rounded ${isDarkMode ? "bg-gray-800 text-blue-400 hover:bg-gray-700" : "bg-white text-blue-500 hover:bg-blue-50"} shadow-sm`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button></Tooltip>
            <Tooltip content="Delete"><button aria-label="Delete" onClick={(e) => { e.stopPropagation(); handleDeleteSingle(file); }}

              className={`p-1 rounded ${isDarkMode ? "bg-gray-800 text-red-400 hover:bg-gray-700" : "bg-white text-red-500 hover:bg-red-50"} shadow-sm`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button></Tooltip>
          </div>
        )}
      </div>
    );
  };

  const renderListRow = (file: StorageFile) => {
    const folder = isFolder(file);
    const fullPath = `${currentPath}/${file.name}`;
    const imageUrl = folder ? "" : getPublicUrl(fullPath);
    const isSelected = selectedIds.has(file.name);

    return (
      <tr key={file.name}
        className={`${isDarkMode ? "hover:bg-gray-700 border-gray-700" : "hover:bg-gray-50 border-gray-200"} border-b transition-colors ${
          !folder && selectedImage === imageUrl ? (isDarkMode ? "bg-blue-900/20" : "bg-blue-50") : ""
        }`}>
        <td className="px-3 py-2">
          <input aria-label={`Select ${file.name}`} type="checkbox" checked={isSelected}
            onChange={() => toggleSelect(file.name)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />
        </td>
        <td className="px-3 py-2">
          <button
            onClick={() => { folder ? navigateInto(file.name) : onSelect(imageUrl); }}
            className={`flex items-center gap-2 text-sm ${getPrimaryText(isDarkMode)} hover:text-blue-500`}>
            {folder
              ? <FolderIcon className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? "text-yellow-400" : "text-yellow-500"}`} />
              : <FileIcon className={`w-4 h-4 flex-shrink-0 ${getMutedText(isDarkMode)}`} />}
            <span className="truncate max-w-xs">{file.name}</span>
          </button>
        </td>
        <td className={`px-3 py-2 text-xs ${getMutedText(isDarkMode)}`}>
          {folder ? "--" : formatBytes(file.metadata?.size as number | undefined)}
        </td>
        <td className={`px-3 py-2 text-xs ${getMutedText(isDarkMode)}`}>
          {file.created_at ? new Date(file.created_at).toLocaleDateString() : "--"}
        </td>
        <td className="px-3 py-2">
          {!folder && (
            <div className="flex items-center gap-1">
              <Tooltip content="Rename"><button aria-label="Rename" onClick={() => setRenameTarget(file)}
                className={`p-1 rounded ${isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button></Tooltip>
              <Tooltip content="Move"><button aria-label="Move" onClick={() => setMoveTarget(file)}
                className={`p-1 rounded ${isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              </button></Tooltip>
              <Tooltip content="Share"><button aria-label="Share" onClick={() => setShareTarget({ fileName: file.name })}
                className={`p-1 rounded ${isDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-500 hover:text-blue-700"}`}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button></Tooltip>
              <Tooltip content="Delete"><Button
  type="button"
  variant="dangerGhost"
  size="sm"
  iconOnly
  aria-label="Delete"
  onClick={() => handleDeleteSingle(file)}
>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </Button></Tooltip>
            </div>
          )}
        </td>
      </tr>
    );
  };

  // =========================================================================
  // Render
  // =========================================================================
  return (
    <div className="flex h-full">
      {confirmModal}
      {/* ── Sidebar ───────────────────────────────────────────────────── */}
      <div className={`w-48 flex-shrink-0 border-r ${getLightBorder(isDarkMode)} ${getPageBackground(isDarkMode)} overflow-auto`}>
        <div className="p-4">
          <h4 className={`text-sm font-semibold ${getSecondaryText(isDarkMode)} mb-3`}>Folders</h4>
          <div className="space-y-1">
            {folders.map((folder) => (
              <button key={folder}
                onClick={() => setSelectedFolder(folder)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                  selectedFolder === folder
                    ? "bg-blue-100 text-blue-700 font-medium"
                    : isDarkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-100"
                }`}>
                <FolderIcon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{getFolderDisplayName(folder)}</span>
              </button>
            ))}
          </div>

          {/* Subfolder tree when browsing into a folder */}
          {subPath.length > 0 && (
            <div className="mt-4">
              <h4 className={`text-xs font-semibold ${getMutedText(isDarkMode)} mb-2 uppercase tracking-wide`}>Subfolders</h4>
              <div className="space-y-1 pl-2">
                {subPath.map((seg, i) => (
                  <button key={i}
                    onClick={() => navigateToBreadcrumb(i)}
                    className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-1.5 transition-colors ${
                      i === subPath.length - 1
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : isDarkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100"
                    }`}
                    style={{ paddingLeft: `${(i + 1) * 8 + 8}px` }}>
                    <FolderIcon className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{seg}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Main area ─────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className={`flex flex-wrap items-center gap-2 px-4 py-3 border-b ${getLightBorder(isDarkMode)}`}>
          {renderBreadcrumb()}

          <div className="flex-1" />

          {/* Search */}
          <div className="relative">
            <input aria-label="Search files"
              type="text"
              placeholder="Search files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-8 pr-3 py-1.5 text-xs rounded-md w-44 ${getInputStyles(isDarkMode)} focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            <svg className={`w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 ${getMutedText(isDarkMode)}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Sort */}
          <select
            value={`${sortColumn}-${sortOrder}`}
            onChange={(e) => {
              const [col, ord] = e.target.value.split("-") as [SortColumn, SortOrder];
              setSortColumn(col);
              setSortOrder(ord);
            }}
            className={`text-xs px-2 py-1.5 rounded-md ${getInputStyles(isDarkMode)} focus:outline-none focus:ring-2 focus:ring-blue-500`}
          >
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
            <option value="created_at-desc">Newest</option>
            <option value="created_at-asc">Oldest</option>
            <option value="size-desc">Largest</option>
            <option value="size-asc">Smallest</option>
          </select>

          {/* View toggle */}
          <div className={`flex rounded-md border ${getLightBorder(isDarkMode)} overflow-hidden`}>
            <Tooltip content="Grid view"><button aria-label="Grid view" onClick={() => setViewMode("grid")}
              className={`p-1.5 ${viewMode === "grid" ? "bg-blue-100 text-blue-700" : isDarkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100"}`}
>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button></Tooltip>
            <Tooltip content="List view"><button aria-label="List view" onClick={() => setViewMode("list")}
              className={`p-1.5 ${viewMode === "list" ? "bg-blue-100 text-blue-700" : isDarkMode ? "text-gray-400 hover:bg-gray-700" : "text-gray-500 hover:bg-gray-100"}`}
>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button></Tooltip>
          </div>

          {/* New Folder */}
          <button onClick={() => { setCreatingFolder(true); setNewFolderName(""); }}
            className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md ${
              isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            } transition-colors`}>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Folder
          </button>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className={`flex items-center gap-3 px-4 py-2 border-b ${getLightBorder(isDarkMode)} ${
            isDarkMode ? "bg-gray-750" : "bg-blue-50"
          }`}>
            <input type="checkbox"
              aria-label="Select all files"
              checked={selectedIds.size === visibleFiles.length && visibleFiles.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />
            <span className={`text-xs font-medium ${getPrimaryText(isDarkMode)}`}>
              {selectedIds.size} selected
            </span>
            <button onClick={handleBulkDelete}
              className="text-xs px-2.5 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors">
              Delete
            </button>
            <button onClick={() => setBulkMoving(true)}
              className={`text-xs px-2.5 py-1 rounded-md ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"} transition-colors`}>
              Move
            </button>
          </div>
        )}

        {/* Inline create-folder input */}
        {creatingFolder && (
          <div className={`flex items-center gap-2 px-4 py-2 border-b ${getLightBorder(isDarkMode)}`}>
            <FolderIcon className={`w-4 h-4 ${isDarkMode ? "text-yellow-400" : "text-yellow-500"}`} />
            <input
              autoFocus
              aria-label="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateFolder();
                if (e.key === "Escape") { setCreatingFolder(false); setNewFolderName(""); }
              }}
              placeholder="Folder name"
              className={`flex-1 px-2 py-1 text-sm rounded ${getInputStyles(isDarkMode)} focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            <button onClick={handleCreateFolder}
              disabled={!newFolderName.trim()}
              className="text-xs px-2.5 py-1 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
              Create
            </button>
            <button onClick={() => { setCreatingFolder(false); setNewFolderName(""); }}
              className={`text-xs px-2 py-1 rounded-md ${isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}>
              Cancel
            </button>
          </div>
        )}

        {/* Content area */}
        <div className="flex-1 overflow-auto p-4"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}>

          {/* Upload drop zone */}
          {(files.length === 0 || isDragging) && (
            <div
              onClick={() => fileInputRef.current?.click()}
              className={`mb-4 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-blue-500 bg-blue-50/10"
                  : isDarkMode ? "border-gray-600 hover:border-blue-500" : "border-gray-300 hover:border-blue-500"
              }`}>
              <input type="file" ref={fileInputRef} multiple accept="image/*"
                aria-label="Upload images"
                onChange={(e) => { if (e.target.files) uploadFiles(e.target.files); e.target.value = ""; }}
                className="hidden" />
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Spinner />
                  <p className={`text-sm ${getSecondaryText(isDarkMode)}`}>Uploading...</p>
                </div>
              ) : (
                <>
                  <svg className={`w-10 h-10 mx-auto mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className={`text-sm ${getSecondaryText(isDarkMode)}`}>
                    Drag &amp; drop files here, or click to browse
                  </p>
                  <p className={`text-xs mt-1 ${getMutedText(isDarkMode)}`}>Images only</p>
                </>
              )}
            </div>
          )}

          {/* Compact upload button when files exist and not dragging */}
          {files.length > 0 && !isDragging && (
            <div className="mb-4 flex justify-end">
              <button onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {isUploading ? <Spinner /> : (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                )}
                {isUploading ? "Uploading..." : "Upload"}
              </button>
              <input type="file" ref={fileInputRef} multiple accept="image/*"
                aria-label="Upload images"
                onChange={(e) => { if (e.target.files) uploadFiles(e.target.files); e.target.value = ""; }}
                className="hidden" />
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-12"><Spinner /></div>
          )}

          {/* Empty state */}
          {!loading && visibleFiles.length === 0 && files.length === 0 && (
            <div className="text-center py-8">
              <svg className={`w-12 h-12 mx-auto ${isDarkMode ? "text-gray-500" : "text-gray-400"} mb-3`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className={getMutedText(isDarkMode)}>No files in this folder</p>
            </div>
          )}

          {/* No search results */}
          {!loading && visibleFiles.length === 0 && files.length > 0 && searchQuery.trim() && (
            <div className="text-center py-8">
              <p className={getMutedText(isDarkMode)}>No files matching "{searchQuery}"</p>
            </div>
          )}

          {/* Grid view */}
          {!loading && visibleFiles.length > 0 && viewMode === "grid" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {visibleFiles.map(renderFileCard)}
            </div>
          )}

          {/* List view */}
          {!loading && visibleFiles.length > 0 && viewMode === "list" && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className={`text-xs uppercase tracking-wider ${
                    isDarkMode ? "text-gray-400 border-gray-700" : "text-gray-500 border-gray-200"
                  } border-b`}>
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox"
                        aria-label="Select all files"
                        checked={selectedIds.size === visibleFiles.length && visibleFiles.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer" />
                    </th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2 w-24">Size</th>
                    <th className="px-3 py-2 w-28">Date</th>
                    <th className="px-3 py-2 w-24">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleFiles.map(renderListRow)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ────────────────────────────────────────────────────── */}
      {renameTarget && (
        <RenameModal
          isDarkMode={isDarkMode}
          currentName={renameTarget.name}
          onConfirm={handleRename}
          onCancel={() => setRenameTarget(null)}
        />
      )}

      {moveTarget && !bulkMoving && (
        <MoveModal
          isDarkMode={isDarkMode}
          folders={folders}
          currentFolder={selectedFolder}
          getFolderDisplayName={getFolderDisplayName}
          onConfirm={handleMove}
          onCancel={() => setMoveTarget(null)}
        />
      )}

      {bulkMoving && (
        <MoveModal
          isDarkMode={isDarkMode}
          folders={folders}
          currentFolder={selectedFolder}
          getFolderDisplayName={getFolderDisplayName}
          onConfirm={(target) => { handleBulkMove(target); setBulkMoving(false); }}
          onCancel={() => setBulkMoving(false)}
        />
      )}

      {shareTarget && (
        <ShareDialog
          isOpen={true}
          onClose={() => setShareTarget(null)}
          isDarkMode={isDarkMode}
          folderPath={shareTarget.folderPath}
          fileName={shareTarget.fileName}
        />
      )}
    </div>
  );
};

export default MediaUploadsPanel;
